import React, { useState, useEffect } from 'react';
import { Menu, X, Search, Facebook, Instagram, Youtube } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // Add/remove class to body when mobile menu opens/closes
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMenuOpen]);

  const navigation = [
    { name: t('navigation.home'), href: '/' },
    { name: t('navigation.blog'), href: '/blog' },
    { name: t('navigation.training'), href: '/training' },
    { name: t('navigation.tournaments'), href: '/tournaments' },
    { name: t('navigation.events'), href: '/events' },
    { name: t('navigation.gallery'), href: '/gallery' },
    { name: t('navigation.about'), href: '/about' },
    { name: t('navigation.contact'), href: '/contact' },
  ];
  const [search, setSearch] = useState('');

  return (
    <header className="fixed top-0 w-full bg-slate-900/95 backdrop-blur-sm z-50 border-b border-amber-500/20">
      <div className="container mx-auto px-4">
        <div className="flex items-center h-20 relative">
          {/* Logo - positioned at far left with preserved aspect ratio */}
          <Link to="/" className={`flex items-center relative z-20 flex-shrink-0 w-fit h-fit transition-opacity duration-300 ${
            isMenuOpen ? 'opacity-30' : 'opacity-100'
          }`}>
            <picture>
              <source 
                media="(max-width: 768px)" 
                srcSet="/lovable-uploads/dc823777-063f-436c-b4fa-108a6a9e5cab.png 200w" 
                width="200" 
                height="78"
              />
              <source 
                media="(min-width: 769px)" 
                srcSet="/lovable-uploads/dc823777-063f-436c-b4fa-108a6a9e5cab.png 309w" 
                width="309" 
                height="120"
              />
              <img 
                src="/lovable-uploads/dc823777-063f-436c-b4fa-108a6a9e5cab.png" 
                alt="ASC Fechten Wien Logo" 
                className="h-auto max-h-[120px] w-auto object-contain relative z-30 mt-12 md:mt-16"
                width="309"
                height="120"
                loading="eager"
                sizes="(max-width: 768px) 200px, 309px"
                style={{ maxWidth: '309px', height: 'auto' }}
              />
            </picture>
          </Link>

          {/* Desktop Navigation - nav stays centered, socials shrink to corner when colliding */}
          <div className="hidden lg:flex flex-1 ml-8 xl:ml-16 h-[80px] relative">
            {/* Navigation container - always vertically centered */}
            <div className="flex items-center h-full flex-1">
              <nav className="flex items-center space-x-6 xl:space-x-8">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`relative text-sm font-medium transition-colors hover:text-amber-400 whitespace-nowrap ${
                      location.pathname === item.href
                        ? 'text-amber-400'
                        : 'text-slate-300'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
            
            {/* Search + Social container - moves to top-right corner when colliding */}
            <div className="flex items-center gap-2 xl:gap-3 pl-3 xl:pl-4 border-l border-amber-500/20 bg-slate-900/30 rounded-l-sm absolute right-0 top-0 mt-2 2xl:relative 2xl:top-auto 2xl:transform-none 2xl:mt-0 h-8 xl:h-10 2xl:h-auto 2xl:min-h-[40px] text-xs xl:text-sm 2xl:text-base">
              {/* Search - Input appears to the left of fixed icon on hover */}
              <div className="relative group flex items-center">
                {/* Input field - appears to the left on hover */}
                <input
                  aria-label={t('header.search')}
                  value={search}
                  onChange={(e)=>setSearch(e.target.value)}
                  placeholder={t('header.searchPlaceholder')}
                  onSubmit={(e)=>{e.preventDefault(); if(search.trim()){ window.location.href = `/search?q=${encodeURIComponent(search.trim())}`}}}
                  onKeyDown={(e)=>{if(e.key==='Enter'){e.preventDefault(); if(search.trim()){ window.location.href = `/search?q=${encodeURIComponent(search.trim())}`}}}}
                  className="opacity-0 group-hover:opacity-100 w-0 group-hover:w-24 xl:group-hover:w-40 px-0 group-hover:px-2 xl:group-hover:px-3 py-1 rounded bg-slate-800/40 text-slate-200 placeholder-slate-400 border border-amber-500/20 focus:outline-none focus:ring-1 focus:ring-amber-500/50 text-xs xl:text-sm transition-all duration-300 mr-2"
                />
                {/* Search icon - stays fixed in position */}
                <Search 
                  size={16} 
                  className="text-slate-400 group-hover:text-amber-400 transition-colors cursor-pointer xl:w-[18px] xl:h-[18px] flex-shrink-0" 
                />
              </div>
              <a
                href="https://www.facebook.com/people/ASC-Fechten/100057153112929/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-blue-400 transition-colors"
                aria-label={t('header.facebookLabel')}
              >
                <Facebook size={16} className="xl:w-[18px] xl:h-[18px]" />
              </a>
              <a
                href="https://www.instagram.com/asc_fechten/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-pink-400 transition-colors"
                aria-label={t('header.instagramLabel')}
              >
                <Instagram size={16} className="xl:w-[18px] xl:h-[18px]" />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-red-400 transition-colors"
                aria-label={t('header.youtubeLabel')}
              >
                <Youtube size={16} className="xl:w-[18px] xl:h-[18px]" />
              </a>
              <LanguageSwitcher />
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-slate-300 hover:text-amber-400 transition-colors relative z-10 ml-auto"
            aria-expanded={isMenuOpen}
            aria-label={
              isMenuOpen
                ? t('header.closeMenu', { defaultValue: 'Menü schließen' })
                : t('header.openMenu', { defaultValue: 'Menü öffnen' })
            }
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-amber-500/20 relative z-[70] bg-slate-900/98 backdrop-blur-sm">
            <nav className="py-4 space-y-2 pt-6">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-2 text-sm font-medium transition-colors hover:text-amber-400 hover:bg-slate-800/50 rounded relative z-50 ${
                    location.pathname === item.href
                      ? 'text-amber-400 bg-slate-800/30'
                      : 'text-slate-300'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Search, Language Switcher and Social Media Icons */}
              <div className="px-4 pt-4 border-t border-amber-500/20 mt-4 space-y-4">
                {/* Mobile Search */}
                <div className="flex items-center gap-2">
                  <input
                    aria-label={t('header.search')}
                    value={search}
                    onChange={(e)=>setSearch(e.target.value)}
                    placeholder={t('header.searchPlaceholder')}
                    onKeyDown={(e)=>{if(e.key==='Enter'){e.preventDefault(); if(search.trim()){ window.location.href = `/search?q=${encodeURIComponent(search.trim())}`}}}}
                    className="flex-1 px-3 py-2 rounded bg-slate-800/40 text-slate-200 placeholder-slate-400 border border-amber-500/20 focus:outline-none focus:ring-1 focus:ring-amber-500/50 text-sm"
                  />
                  <button
                    onClick={() => {
                      if(search.trim()){ 
                        window.location.href = `/search?q=${encodeURIComponent(search.trim())}`
                      }
                    }}
                    className="p-2 text-slate-400 hover:text-amber-400 transition-colors"
                    aria-label={t('header.search')}
                  >
                    <Search size={20} />
                  </button>
                </div>
                
                {/* Language Switcher and Social Icons */}
                <div className="flex items-center justify-center space-x-6">
                  <LanguageSwitcher />
                  <a
                    href="https://www.facebook.com/people/ASC-Fechten/100057153112929/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-blue-400 transition-colors"
                  >
                    <Facebook size={24} />
                  </a>
                  <a
                    href="https://www.instagram.com/asc_fechten/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-pink-400 transition-colors"
                  >
                    <Instagram size={24} />
                  </a>
                  <a
                    href="https://youtube.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-red-400 transition-colors"
                  >
                    <Youtube size={24} />
                  </a>
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;